import { GameEvent } from "../enums/GameEvent";
import { Page } from "../enums/Page";
import { html } from "../helpers/webComponents";
import { GameEventService, SwitchPageEvent } from "../services/GameEventService";

/** Default page used by the {@link RootComponent} */
export const DefaultPage: Page = Page.Canvas;

/**
 * A "router" Web Component
 *
 * @remarks Will listen for the Switch Page event and render the requested page if possible. Otherwise, switches to a Not Found page.
 */
export class RootComponent extends HTMLElement {
    /** Instance of the game event service */
    private readonly _gameEventService: GameEventService = new GameEventService();

    /** Current active page */
    private _currentPage: Page = DefaultPage;

    /**
     * The "constructor" of a Web Component
     */
    public connectedCallback(): void {
        this.attachShadow({ mode: "open" });

        // Listen for the Switch Page event
        this._gameEventService.addGameEventListener<SwitchPageEvent>(GameEvent.SwitchPage, event => {
            this.handleSwitchPage(event.page);
        });

        this.render();
    }

    /**
     * Render the contents of this page
     */
    private render(): void {
        if (!this.shadowRoot) {
            return;
        }

        const element: HTMLElement = this.renderCurrentPage();

        // When loading Canvas replace all children
        if (this._currentPage === Page.Canvas) {
            this.shadowRoot.replaceChildren(element);
            return;
        }
        if (this._currentPage === Page.Wordle) {
            this.shadowRoot.replaceChildren(element);
            return;
        }
        if (this._currentPage === Page.Dance) {
            this.shadowRoot.replaceChildren(element);
            return;
        }

        // When loading something other then Canvas we expect this to be an overlay. So delete everything except for the game-canvas
        for (const child of Object.values(this.shadowRoot.children)) {
            if (child.tagName.toLowerCase() !== "game-canvas") {
                child.remove();
            }
        }

        this.shadowRoot.append(element);
    }

    /**
     * Render the current active page
     *
     * @returns HTML element of the current active page
     */
    private renderCurrentPage(): HTMLElement {
        switch (this._currentPage) {
            case Page.Wordle:
                return html`<game-wordle></game-wordle>`;
            case Page.Dance:
                return html`<game-dance></game-dance>`;
            case Page.Keypad:
                return html`<game-keypad></game-keypad>`;
            case Page.Canvas:
                return html`<game-canvas></game-canvas>`;
            case Page.GameBinos:
                return html`<game-binos></game-binos>`;
            default:
                return html`<game-notfound page="${this._currentPage}"></game-notfound>`;
        }
    }

    /**
     * Switch to a different page
     *
     * @param page Page to switch to
     */
    private handleSwitchPage(page: Page): void {
        this._currentPage = page;

        this.render();
    }
}
